import { Send } from "lucide-react-native";
import { ComponentRef, FC, useEffect, useRef, useState } from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from "react-native";

import {
  Box,
  Button,
  ButtonIcon,
  Card,
  Center,
  ErrorWithRetry,
  HStack,
  Input,
  InputField,
  Skeleton,
  Text,
  TypingIndicator,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { StudyChatMessage } from "@/types/resources/studies/chat";

interface StudyChatProps {
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  runId: string;
  studyId: string;
}

const StudyChat: FC<StudyChatProps> = ({ onScroll, runId, studyId }) => {
  const { i18n } = useLocalization();
  const { error, getChat, loading, messages } = API.Studies.useGetChat(
    studyId,
    runId
  );
  const {
    clearError,
    error: sendError,
    loading: sending,
    sendMessage,
  } = API.Studies.useSendChatMessage(studyId, runId);

  const [localMessages, setLocalMessages] =
    useState<StudyChatMessage[]>(messages);
  const [inputHeight, setInputHeight] = useState(40);
  const [inputMessage, setInputMessage] = useState("");
  const scrollViewRef = useRef<ComponentRef<typeof ScrollView>>(null);

  // Sync local messages from the fetched messages during render (not in an
  // effect) whenever they change.
  const [prevMessages, setPrevMessages] = useState(messages);
  if (prevMessages !== messages) {
    setPrevMessages(messages);
    setLocalMessages(messages);
  }

  const displayMessages = localMessages.length > 0 ? localMessages : messages;

  useEffect(() => {
    if (displayMessages.length > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [displayMessages.length, sending]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || sending) return;

    if (sendError) clearError();

    const messageText = inputMessage;
    const userMessage: StudyChatMessage = {
      content: messageText,
      created_at: new Date().toISOString(),
      id: `temp-${Date.now()}`,
      role: "user",
    };

    setLocalMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setInputHeight(40);

    const result = await sendMessage({
      message: messageText,
    });

    if (result) {
      setLocalMessages(result);
    } else {
      // Remove user message and restore input on error
      setLocalMessages((prev) => prev.slice(0, -1));
      setInputMessage(messageText);
    }
  };

  const renderMessage = (message: StudyChatMessage) => {
    const isUser = message.role === "user";

    return isUser ? (
      <HStack className="justify-end">
        <Card className="max-w-[85%] min-w-[200px] p-4 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-2xl rounded-tr-sm">
          <Text className="text-sm text-purple-900 dark:text-purple-100 leading-relaxed">
            {message.content}
          </Text>
        </Card>
      </HStack>
    ) : (
      <HStack className="justify-start">
        <Card className="max-w-[85%] p-4 bg-white dark:bg-outline-800 border border-outline-200 dark:border-outline-700 rounded-2xl rounded-tl-sm">
          <Text className="text-sm text-typography-700 dark:text-typography-300 leading-relaxed">
            {message.content}
          </Text>
        </Card>
      </HStack>
    );
  };

  return (
    <VStack className="flex-1">
      <ScrollView
        className="flex-1"
        contentContainerClassName="p-4"
        onScroll={onScroll}
        ref={scrollViewRef}
        scrollEventThrottle={16}
      >
        {loading && (
          <VStack className="gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <HStack
                key={i}
                className={i % 2 === 0 ? "justify-end" : "justify-start"}
              >
                <Skeleton
                  className={`h-20 rounded-2xl ${
                    i % 2 === 0
                      ? "w-[75%] rounded-tr-sm"
                      : "w-[70%] rounded-tl-sm"
                  }`}
                />
              </HStack>
            ))}
          </VStack>
        )}

        {error && (
          <Center className="flex-1 py-12">
            <ErrorWithRetry
              error={error}
              onRetry={getChat}
              title={i18n.t("studies.errorLoadingChat")}
            />
          </Center>
        )}

        {!loading && !error && (
          <>
            {displayMessages.length === 0 ? (
              <VStack className="items-center justify-center py-12 gap-4">
                <Text className="text-center text-lg font-medium text-typography-700 dark:text-typography-300">
                  {i18n.t("studies.noChatMessages")}
                </Text>
                <Text className="text-center text-sm text-typography-500 mt-2">
                  {i18n.t("studies.startChattingWithAI")}
                </Text>
                {sendError && (
                  <Text className="text-sm text-error-500 mt-2 text-center">
                    {sendError}
                  </Text>
                )}
                <TypingIndicator isTyping={sending} />
              </VStack>
            ) : (
              <VStack className="gap-4">
                {displayMessages.map((message) => (
                  <Box key={message.id}>{renderMessage(message)}</Box>
                ))}
                <TypingIndicator isTyping={sending} />
                {sendError && (
                  <Text className="text-sm text-error-500 mt-2 text-center">
                    {sendError}
                  </Text>
                )}
              </VStack>
            )}
          </>
        )}
      </ScrollView>

      {/* Input Area */}
      <HStack className="p-4 border-t border-outline-200 dark:border-outline-700 bg-white dark:bg-background-900 gap-2 items-end">
        <Input
          className="flex-1 h-auto border border-outline-200 rounded-xl bg-background-50"
          isDisabled={sending || loading}
          style={{ minHeight: 40, maxHeight: 120, height: inputHeight }}
        >
          <InputField
            className="text-typography-700 dark:text-typography-300 py-2"
            maxLength={5000}
            multiline
            onChangeText={(text) => {
              setInputMessage(text);
              if (sendError) clearError();
            }}
            onContentSizeChange={(event) => {
              const newHeight = event.nativeEvent.contentSize.height;
              setInputHeight(Math.min(Math.max(newHeight, 40), 120));
            }}
            onKeyPress={(event) => {
              if (event.nativeEvent.key === "Enter") {
                if (!event.nativeEvent.shiftKey) {
                  event.preventDefault();
                  if (inputMessage.trim() && !sending && !loading) {
                    handleSendMessage();
                  }
                }
              }
            }}
            placeholder={i18n.t("studies.typeYourMessage")}
            value={inputMessage}
          />
        </Input>
        <Button
          className="bg-primary-500"
          isDisabled={!inputMessage.trim() || sending || loading}
          onPress={handleSendMessage}
          size="md"
        >
          <ButtonIcon as={Send} />
        </Button>
      </HStack>
    </VStack>
  );
};

export default StudyChat;
